import React, { Children } from "react";
import styled from "styled-components";
import { sanitize } from 'dompurify';
import { FormSectionElements, FormSubSections, ProgramContainer } from "../../entities/formTemplateDataType";
import { Heading2, spacing, TextParagraph } from "../../theme";
import { findAddressElement, getSubSectionButton } from "../../utilities/ElementUtils";
import { ReactComponent as EditIcon } from "../../../public/assets/css/ontario-design-system/icons/svg/ontario-icon-edit.svg";
import { splitAddress } from "../../utilities/addressUtils";
import DataUtils from "../../utilities/DataUtils";
import colors from "../../theme/variables/colors";

const InfoElementContainer = styled.div`
    border-bottom: ${props => (props.className?.includes('notBordered') ? '0px' : '2px')} solid ${colors.palette.greyscale5};
    margin-top: ${spacing.spacing5};
    min-width: 253.33px;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    align-items: start;
    justify-content: space-between;
    gap: 1rem;
`;

const StyledHeading = styled(Heading2)`
    display: inline;
`;

const SplitRight = styled.div`
    display: flex;
    width: auto;
    justify-content: space-between;
    gap: 1rem;

    @media (max-width: 480px) {
        flex-wrap: wrap;
    }
`;

const Text = styled(TextParagraph)`
    margin: ${spacing.spacing0} ${spacing.spacing0} ${spacing.spacing3} ${spacing.spacing0};
`;

interface Props {
    field: FormSectionElements;
    isEng: boolean;
    withButton: boolean;
    handleEditButtonClick?: (field: FormSectionElements) => void;
    callback: (programContainer: ProgramContainer) => void;
    children?: string | React.ReactNode;
}

const TableSummaryComponent: React.FC<Props> = ({
    field, isEng, withButton, handleEditButtonClick, callback, children
}: Props) => {
    const splitAddressFromField = (summaryField: FormSectionElements) => {
        const addressField = findAddressElement(summaryField);
        if (addressField) {
            return splitAddress(addressField.Address)
        }
        return '';
    }
    const getValue = (field: FormSectionElements) => {
        let value = '';
        if (field.MaskInSummary) {
            switch (field.Datatype) {
                case "ODSDate":
                    value = field.mask ? "****/**/**" : field.Value;
                    break;
                case "Address":
                    // value= this.splitAddress((field.Address || this.profile?.address) as Address | undefined)
                    value = splitAddressFromField(field)
                    break;
                case "Email":
                    const emailParts = field.Value?.split('@');
                    let maskedValue = emailParts[0].substring(0, 2) + '******';
                    if (emailParts.length > 1) {
                        const emailDomainParts = emailParts[1].split('.');
                        maskedValue += `@${emailDomainParts[0].substring(0, 1)}*****`;
                        if (emailDomainParts.length > 1) {
                            maskedValue += `.${emailDomainParts[1]}`;
                        }
                    }
                    value = field.mask? maskedValue:field.Value;
                    break;
                default:
                    const len = field.Value?.length;
                    if (len > 3) {
                        const maskLength = len - 3;
                        value = field.mask ? DataUtils.maskString(field.Value, 0, maskLength) : field.Value;
                    }
            }
        } else {
            value = field.Value;
        }
        return value;
    }

    return (
        <InfoElementContainer className={`info-element`}>
            <StyledHeading styledAs="h4">
                {isEng ? field.ENUTitle : field.FRATitle}
            </StyledHeading>
                
            <SplitRight id="infoelementsplit">
                <Text
                    dangerouslySetInnerHTML={{
                        __html: getValue(field) || '',
                    }}
                />{' '}
                <button style={{display: 'flex', alignItems: 'start', border: 'none', backgroundColor: '#fff', cursor: 'pointer', textDecoration: 'underline', padding: '0.25rem 0'}} 
                    title={isEng ? "Edit" : "Modifier"}
                    onClick={() => {
                        const subSectionButton = getSubSectionButton(field.parent as FormSubSections);
                        handleEditButtonClick(subSectionButton)
                    }}
                >
                    {/* <EditIcon aria-hidden='true' fill='#00478f'/> */}
                    <span style={{color: "#00478f", fontSize: "1.2rem"}}>{isEng ? "Change" : "Modifier"}</span>
                </button>
            </SplitRight>
            {children}
        </InfoElementContainer>
    );
}

export default TableSummaryComponent;